import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../../global/Alert/Alert';
import SharedButton from '../../global/Button/Button';
import { userAppContext } from '../../context/Context';
import { getFilters, deleteFilter, updateFilter } from '../../services/savedFiltersService';
import { UserContext } from '../../types';
import { SavedSearch, Filter, ProfileFavoritesProps, TabType } from './types';
import { filterColumnFilters, determineSearchType, getEmptyMessage } from './favoritesUtils';
import SearchCard from './SearchCard';
import TabSelector from '@/global/TabSelector/TabSelector';
import { buildSearchUrl } from '../../modules/search/searchGrid/components/table/components/rightAsideSidebar.utils';
import {
  encodeData,
  encodeFiltersToParams,
  removePanelFiltersFromSearchParams,
} from '../../utils/encodeUtils';

const TAB_OPTIONS: { value: TabType; label: string }[] = [
  { value: 'searches', label: 'SAVED SEARCHES' },
  { value: 'columns', label: 'SAVED COLUMNS' },
  { value: 'scores', label: 'SAVED SCORES' },
];

const ProfileFavorites = ({ favoritesClass }: ProfileFavoritesProps) => {
  const navigate = useNavigate();
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('searches');

  const { isAuthenticated } = userAppContext() as unknown as UserContext;

  const handleTabChange = (value: string) => {
    if (value === 'searches' || value === 'columns' || value === 'scores') {
      setActiveTab(value);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadSavedSearches();
    }
  }, [isAuthenticated]);

  const loadSavedSearches = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getFilters();
      const cloudFilters = response.filters || [];

      const searches = cloudFilters.map((filter: any) => {
        const isEvidence =
          filter.searchContext?.isEvidence !== undefined
            ? Boolean(filter.searchContext.isEvidence)
            : Boolean(filter.isEvidence);
        return {
          id: filter.filterId,
          table: filter.type === 'score' ? 'Score' : isEvidence ? 'Evidence' : 'Summary',
          filters: filter.filters,
          searchQuery: filter.searchQuery,
          savedAt: filter.createdAt,
          name: filter.name,
          type: determineSearchType(filter),
          columnOrder: filter.columnOrder,
          columnVisibilityModel: filter.columnVisibilityModel,
          searchContext: filter.searchContext,
          sourceWeights: filter.sourceWeights,
          isLocal: false,
        };
      });

      const localColumnSelections = loadLocalColumnSelections();
      const localScores = loadLocalScores();

      setSavedSearches([...searches, ...localColumnSelections, ...localScores]);
    } catch (err) {
      console.error('Error loading saved searches:', err);
      setError('Failed to load saved searches');
    } finally {
      setIsLoading(false);
    }
  };

  const loadLocalColumnSelections = (): SavedSearch[] => {
    const columnSelections: SavedSearch[] = [];
    const keys = Object.keys(localStorage);

    keys.forEach((key: string) => {
      if (key.startsWith('columnSelection_') || key.startsWith('completeSearch_')) {
        try {
          const selection = JSON.parse(localStorage.getItem(key) || '');
          columnSelections.push({
            id: key,
            table: selection.searchContext?.isEvidence ? 'Evidence' : 'Summary',
            filters: selection.columnFilters || selection.columnVisibilityModel || [],
            searchQuery: selection.searchQuery || null,
            savedAt: selection.savedAt,
            name: selection.name,
            type: key.startsWith('completeSearch_') ? 'complete' : 'columns',
            columnOrder: selection.columnOrder,
            columnVisibilityModel: selection.columnVisibilityModel,
            searchContext: selection.searchContext,
            isLocal: true,
          });
        } catch (error) {
          console.error('Error parsing saved selection:', error);
        }
      }
    });

    return columnSelections.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
  };

  const loadLocalScores = (): SavedSearch[] => {
    const scores: SavedSearch[] = [];
    const keys = Object.keys(localStorage);

    keys.forEach((key: string) => {
      if (key.startsWith('savedScore_')) {
        try {
          const scoreData = JSON.parse(localStorage.getItem(key) || '');
          scores.push({
            id: key,
            table: 'Score',
            filters: [],
            searchQuery: null,
            savedAt: scoreData.savedAt,
            name: scoreData.name,
            type: 'score',
            sourceWeights: scoreData.sourceWeights,
            searchContext: scoreData.searchContext,
            isLocal: true,
          });
        } catch (error) {
          console.error('Error parsing saved score:', error);
        }
      }
    });

    return scores.sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
  };

  const getFilteredSearches = (): SavedSearch[] => {
    return savedSearches.filter((search: SavedSearch) => {
      if (activeTab === 'searches') {
        return search.type === 'search' || search.type === 'complete' || search.type === 'filter';
      }
      if (activeTab === 'columns') return search.type === 'columns';
      if (activeTab === 'scores') return search.type === 'score';
      return false;
    });
  };

  const filteredSearches = getFilteredSearches();

  const handleNavigateToSearch = (search: SavedSearch) => {
    const targetUrl = search.searchQuery
      ? buildSearchUrl(search)
      : buildSearchUrl({
          ...search,
          filters: (search.filters || []).map((f: Filter) => ({
            ...f,
            isEvidence:
              f.isEvidence !== undefined
                ? f.isEvidence
                : (search.searchContext?.isEvidence ?? search.table === 'Evidence'),
          })),
        });

    if (targetUrl) {
      const url = new URL(targetUrl);
      if (search.type === 'complete' && search.searchQuery) {
        url.searchParams.set('loadComplete', search.id);
      }
      navigate(url.href.replace(window.location.origin, ''));
    }
  };

  const handleNavigateWithColumns = (search: SavedSearch) => {
    if (!search.searchContext) return;

    const { view, tab, source } = search.searchContext;
    const url = new URL(`${window.location.origin}/search`);
    const isComplete = search.type === 'complete';

    // Set base parameters
    if (isComplete && search.searchQuery) {
      const searchParams = new URLSearchParams(search.searchQuery);
      searchParams.forEach((value, key) => url.searchParams.set(key, value));
    } else {
      url.searchParams.set('view', view || 'ALL');
      url.searchParams.set('tab', tab || 'GDA');
      url.searchParams.set('source', source || 'ALL');

      // Use searchItems if available, otherwise use 'ALL'
      if (search.searchContext.searchItems && search.searchContext.searchItems.length > 0) {
        const idents = search.searchContext.searchItems.map(item => item.id).join('-');
        url.searchParams.set('idents', idents);
      } else {
        url.searchParams.set('idents', 'ALL');
      }
    }

    // Add filters for complete searches (preserve per-filter panel)
    if (isComplete && search.filters?.length) {
      const columnFilters = filterColumnFilters(search.filters);
      if (columnFilters.length > 0) {
        const fallbackIsEvidence = Boolean(search.searchContext.isEvidence);
        const filtersWithPanel = columnFilters.map((f: Filter) => ({
          ...f,
          isEvidence: f.isEvidence !== undefined ? Boolean(f.isEvidence) : fallbackIsEvidence,
          operator: f.operator ?? 'eq',
          type: f.type ?? 'string',
        }));
        removePanelFiltersFromSearchParams(url.searchParams, 'evidence');
        removePanelFiltersFromSearchParams(url.searchParams, 'summary');
        encodeFiltersToParams(filtersWithPanel).forEach((value, key) => {
          url.searchParams.set(key, value);
        });
        url.searchParams.set('f', encodeData(filtersWithPanel));
      }
    }

    if (search.searchContext.summaryMatch || search.searchContext.isEvidence === false) {
      url.searchParams.set('activePanel', 'summary');
      url.searchParams.delete('isEvidence');
    } else if (search.searchContext.isEvidence === true) {
      url.searchParams.set('activePanel', 'evidence');
    } else if (!isComplete && search.searchContext.isEvidence !== undefined) {
      url.searchParams.set('isEvidence', String(search.searchContext.isEvidence));
    }

    url.searchParams.set(isComplete ? 'loadComplete' : 'loadColumns', search.id);
    navigate(url.href.replace(window.location.origin, ''));
  };

  const handleNavigateWithScore = (search: SavedSearch) => {
    if (!search.searchContext || !search.sourceWeights) return;

    const { view, tab, source, searchItems } = search.searchContext;
    const url = new URL(`${window.location.origin}/search`);

    url.searchParams.set('view', view || 'ALL');
    url.searchParams.set('tab', tab || 'GDA');
    url.searchParams.set('source', search.sourceWeights.length > 1 ? 'ALL' : source || 'ALL');

    if (searchItems && searchItems.length > 0) {
      const idents = searchItems.map(item => item.id).join(',');
      url.searchParams.set('idents', idents);
    } else {
      url.searchParams.set('idents', 'ALL');
    }

    sessionStorage.setItem(`sourceWeights_${tab}`, JSON.stringify(search.sourceWeights));

    navigate(url.href.replace(window.location.origin, ''));
  };

  const handleDeleteSearch = async (searchId: string) => {
    try {
      const searchToDelete = savedSearches.find(s => s.id === searchId);

      if (searchToDelete?.isLocal) {
        localStorage.removeItem(searchId);
      } else {
        await deleteFilter(searchId);
      }

      const updatedSearches = savedSearches.filter(s => s.id !== searchId);
      setSavedSearches(updatedSearches);

      setShowDeleteAlert(true);
      setTimeout(() => setShowDeleteAlert(false), 3000);
    } catch (error) {
      console.error('Error deleting search:', error);
      setError('Failed to delete search');
    }
  };

  const handleClearAllSearches = async () => {
    try {
      const searchesToDelete = filteredSearches;

      const localItems = searchesToDelete.filter(s => s.isLocal);
      const cloudItems = searchesToDelete.filter(s => !s.isLocal);

      localItems.forEach(item => localStorage.removeItem(item.id));

      if (cloudItems.length > 0) {
        await Promise.all(cloudItems.map(search => deleteFilter(search.id)));
      }

      const remainingSearches = savedSearches.filter(s => !searchesToDelete.find(d => d.id === s.id));
      setSavedSearches(remainingSearches);
      setShowDeleteAlert(true);
      setTimeout(() => setShowDeleteAlert(false), 3000);
    } catch (error) {
      console.error('Error clearing all searches:', error);
      setError('Failed to clear all searches');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className={favoritesClass} id="favorites">
        <h2>Saved Searches</h2>
        <div className="wrap-input100" />
        <div style={{ padding: '20px 0' }}>
          <p className="label-input100" style={{ color: '#999' }}>
            Please log in to see your saved searches.
          </p>
        </div>
      </div>
    );
  }

  const handleSaveEdit = async (filterId: string) => {
    if (!editName.trim()) return;

    try {
      await updateFilter(filterId, { name: editName });

      setSavedSearches(prev => prev.map(s => (s.id === filterId ? { ...s, name: editName } : s)));

      setEditId(null);
      setEditName('');
      setShowUpdateAlert(true);
      setTimeout(() => setShowUpdateAlert(false), 3000);
    } catch (error) {
      console.error('Error updating filter name:', error);
      setError('Failed to update filter name');
    }
  };

  return (
    <>
      {showDeleteAlert && <Alert message="Search deleted successfully" type="success" />}
      {showUpdateAlert && <Alert message="Filter saved successfully!" type="success" />}
      {error && <Alert message={error} type="error" />}

      <div className={favoritesClass}>
        <h5>Saved Searches</h5>
        <div className="bg-white border rounded-xl p-4 mb-4">
          {/* Tabs */}
          <div
            className="ms-radio-group ms-radio-group-mobile"
            onChange={e => {
              const value = (e.target as HTMLInputElement)?.value;
              if (value) handleTabChange(value);
            }}
          >
            <div className="mb-6">
              <TabSelector
                options={TAB_OPTIONS}
                variant="primary"
                size="sm"
                value={activeTab}
                onChange={handleTabChange}
              />
            </div>
          </div>

          {isLoading ? (
            <div style={{ padding: '40px 0', textAlign: 'center' }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden"></span>
              </div>
              <p style={{ marginTop: '16px', color: '#666' }}>Loading your saved searches...</p>
            </div>
          ) : filteredSearches.length === 0 ? (
            <div style={{ padding: '20px 0' }}>
              <p className="label-input100" style={{ color: '#999' }}>
                {getEmptyMessage(activeTab)}
              </p>
            </div>
          ) : (
            <>
              <div className="d-flex mb-3 justify-content-end mr-2">
                {filteredSearches.length > 0 && (
                  <SharedButton
                    id="clearAll"
                    variant="secondary"
                    size="xs"
                    onClick={handleClearAllSearches}
                    type="button"
                    className="BtnAccion mx-0 mb-2"
                    style={{ height: '30px', width: '8em', fontSize: '12px' }}
                  >
                    Clear All
                  </SharedButton>
                )}
              </div>

              <div style={{ marginTop: '20px' }}>
                {filteredSearches.map(search => (
                  <SearchCard
                    key={search.id}
                    search={search}
                    activeTab={activeTab}
                    editId={editId}
                    editName={editName}
                    onEditChange={setEditName}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={() => {
                      setEditId(null);
                      setEditName('');
                    }}
                    onStartEdit={(id, name) => {
                      setEditId(id);
                      setEditName(name);
                    }}
                    onNavigate={handleNavigateToSearch}
                    onNavigateWithColumns={handleNavigateWithColumns}
                    onNavigateWithScore={handleNavigateWithScore}
                    onDelete={handleDeleteSearch}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ProfileFavorites;
